import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  TextField,
  Switch,
  InputAdornment,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Parse from "parse";
import { useNotify, useRefresh } from "react-admin";

const GlobalSettingsDialog = ({ open, onClose }) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const [rechargeEnabled, setRechargeEnabled] = useState(true);
  const [cashoutEnabled, setCashoutEnabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [agents, setAgents] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1); // current page
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 10; // show 10 agents per page

  useEffect(() => {
    if (open) {
      fetchAgents();
      fetchSettings();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchAgents();
    }
  }, [open, searchTerm, page]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const rechargeSetting = await new Parse.Query("Settings")
        .equalTo("type", "rechargeEnabled")
        .first({ useMasterKey: true });
      const cashoutSetting = await new Parse.Query("Settings")
        .equalTo("type", "cashoutEnabled")
        .first({ useMasterKey: true });

      if (rechargeSetting) {
        setRechargeEnabled(rechargeSetting.get("settings")?.[0] === "true");
      }
      if (cashoutSetting) {
        setCashoutEnabled(cashoutSetting.get("settings")?.[0] === "true");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const [rechargeList, cashoutList] = await Promise.all([
        new Parse.Query("Settings")
          .equalTo("type", "allowedAgentsForRecharge")
          .first({ useMasterKey: true }),
        new Parse.Query("Settings")
          .equalTo("type", "allowedAgentsForCashout")
          .first({ useMasterKey: true }),
      ]);
      const allowedRechargeIds = rechargeList?.get("settings") || [];
      const allowedCashoutIds = cashoutList?.get("settings") || [];

      const roleQuery = new Parse.Query(Parse.Role);
      roleQuery.equalTo("name", "Agent");
      const agentRole = await roleQuery.first({ useMasterKey: true });
      if (!agentRole) return;

      const relation = agentRole.relation("users");
      let query = relation.query();

      if (searchTerm.trim()) {
        const regex = `.*${searchTerm.trim()}.*`;
        query.matches("username", regex, "i");
      }

      // pagination
      query.limit(pageSize);
      query.skip((page - 1) * pageSize);

      const totalCount = await query.count({ useMasterKey: true });
      setTotalPages(Math.ceil(totalCount / pageSize));

      const users = await query.find({ useMasterKey: true });
      setAgents(
        users.map((u) => ({
          id: u.id,
          username: u.get("username"),
          rechargeEnabled: allowedRechargeIds.includes(u.id),
          cashoutEnabled: allowedCashoutIds.includes(u.id),
        }))
      );
    } catch (err) {
      console.error("Fetch agents failed", err);
    }
  };

  const handleAgentToggle = (id, key, checked) => {
    setAgents((prev) =>
      prev.map((a) => (a.id === id ? { ...a, [key]: checked } : a))
    );
  };

  const save = async () => {
    setLoading(true);
    try {
      const saveSetting = async (type, value) => {
        let obj = await new Parse.Query("Settings")
          .equalTo("type", type)
          .first({ useMasterKey: true });

        if (!obj) {
          const Settings = Parse.Object.extend("Settings");
          obj = new Settings();
          obj.set("type", type);
        }

        obj.set("settings", value);
        await obj.save(null, { useMasterKey: true });
      };

      await Promise.all([
        saveSetting("rechargeEnabled", [rechargeEnabled.toString()]),
        saveSetting("cashoutEnabled", [cashoutEnabled.toString()]),
        saveSetting(
          "allowedAgentsForRecharge",
          agents.filter((a) => a.rechargeEnabled).map((a) => a.id)
        ),
        saveSetting(
          "allowedAgentsForCashout",
          agents.filter((a) => a.cashoutEnabled).map((a) => a.id)
        ),
      ]);

      notify("✅ Settings updated successfully!", { type: "success" });
      refresh();
      onClose();
    } catch (e) {
      notify("❌ Error saving settings", { type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#2a2a2a",
          borderRadius: "8px",
          color: "white",
          "& .MuiTypography-root": { fontSize: "14px" },
        },
      }}
    >
      <DialogTitle sx={{ color: "white", fontWeight: 600, fontSize: "16px" }}>
        Global Recharge & Redeem Settings
      </DialogTitle>
      <DialogContent dividers sx={{ color: "white" }}>
        {successMsg && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMsg}
          </Alert>
        )}

        {/* Global Toggles */}
        <Box display="flex" flexDirection="column" gap={2}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            bgcolor="#1f1f1f"
            p={1.5}
            borderRadius={1}
          >
            <Typography color="white">Enable Recharge (Global)</Typography>
            <Switch
              checked={rechargeEnabled}
              onChange={(e) => setRechargeEnabled(e.target.checked)}
            />
          </Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            bgcolor="#1f1f1f"
            p={1.5}
            borderRadius={1}
          >
            <Typography color="white">Enable Redeem (Global)</Typography>
            <Switch
              checked={cashoutEnabled}
              onChange={(e) => setCashoutEnabled(e.target.checked)}
            />
          </Box>
        </Box>

        {/* Overrides */}
        {(!rechargeEnabled || !cashoutEnabled) && (
          <>
            <Typography variant="h6" mt={3} mb={1} color="white" sx={{ fontSize: "14px" }}>
              Agent Overrides
            </Typography>
            <TextField
              fullWidth
              placeholder="Search Agents"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1); // reset to first page on new search
              }}
              size="small"
              sx={{
                input: { color: "white", fontSize: "14px" },
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#1f1f1f",
                  "& fieldset": { borderColor: "#444" },
                  "&:hover fieldset": { borderColor: "#777" },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "#aaa" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Box mt={2}>
              {agents.length ? (
                <>
                  {agents.map((agent) => (
                    <Box
                      key={agent.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid #444",
                        py: 1,
                      }}
                    >
                      <Typography sx={{ flex: 1, color: "white", fontSize: "14px" }}>
                        {agent.username}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={agent.rechargeEnabled}
                            onChange={(e) =>
                              handleAgentToggle(agent.id, "rechargeEnabled", e.target.checked)
                            }
                            sx={{ color: "yellow" }}
                          />
                        }
                        label="Recharge"
                        sx={{ color: "white", "& .MuiFormControlLabel-label": { fontSize: "13px" } }}
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={agent.cashoutEnabled}
                            onChange={(e) =>
                              handleAgentToggle(agent.id, "cashoutEnabled", e.target.checked)
                            }
                            sx={{ color: "yellow" }}
                          />
                        }
                        label="Redeem"
                        sx={{ color: "white", "& .MuiFormControlLabel-label": { fontSize: "13px" } }}
                      />
                    </Box>
                  ))}
                  <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(e, val) => setPage(val)}
                      sx={{
                        "& .MuiPaginationItem-root": {
                          color: "white",
                        },
                        "& .Mui-selected": {
                          bgcolor: "#FFD600 !important",
                          color: "black",
                        },
                      }}
                    />
                  </Box>
                </>
              ) : (
                <Typography color="gray" textAlign="center" py={2} sx={{ fontSize: "13px" }}>
                  No agents found
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={onClose}
          sx={{
            bgcolor: "#333",
            color: "white",
            textTransform: "none",
            px: 4,
            fontSize: "14px",
            "&:hover": { bgcolor: "#444" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={loading}
          sx={{
            bgcolor: "#FFD600",
            color: "black",
            textTransform: "none",
            px: 4,
            fontWeight: 600,
            fontSize: "14px",
            "&:hover": { bgcolor: "#FFC107" },
          }}
          startIcon={loading && <CircularProgress size={18} color="inherit" />}
        >
          {loading ? "Saving..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GlobalSettingsDialog;
